<template>
  <div id="app" class="min-h-screen">
    <!-- Backend unreachable: full-page error with auto-retry countdown -->
    <div
      v-if="networkStore.backendUnreachable"
      class="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-6"
      role="alert"
    >
      <div class="max-w-md w-full text-center bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
        <div class="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
          <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Server unavailable</h1>
        <p class="text-gray-600 dark:text-gray-300 mb-2">{{ networkStore.backendUnreachableMessage || 'Cannot reach the server.' }}</p>
        <p class="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Retrying in <strong>{{ retryCountdown }}</strong> second{{ retryCountdown === 1 ? '' : 's' }}…
        </p>
        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            class="btn btn-primary"
            @click="retryHealthCheck"
          >
            Retry now
          </button>
        </div>
      </div>
    </div>
    <template v-else>
      <router-view />
      <AppDialog />
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useNetworkStore } from '@/stores/network';
import { BACKEND_HEALTH_CHECK_INTERVAL_MS } from '@/stores/network';
import { useUnsavedChangesStore } from '@/stores/unsavedChanges';
import AppDialog from '@/components/AppDialog.vue';
import apiClient from '@/api/client';

const RETRY_AFTER_SECONDS = 30;

const networkStore = useNetworkStore();
const unsavedStore = useUnsavedChangesStore();
const retryCountdown = ref(RETRY_AFTER_SECONDS);

function handleBeforeUnload(e: BeforeUnloadEvent) {
  if (unsavedStore.hasUnsaved) {
    e.preventDefault();
    e.returnValue = '';
  }
}

let healthCheckIntervalId: ReturnType<typeof setInterval> | null = null;
let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

async function checkBackendHealth() {
  try {
    await apiClient.get('/health', { timeout: 8000 });
    networkStore.clearBackendUnreachable();
  } catch {
    const message = import.meta.env.DEV
      ? 'Cannot reach the server. Please try again later.'
      : 'Cannot reach the server. Please try again later.';
    networkStore.setBackendUnreachable(message);
  }
}

function retryHealthCheck() {
  retryCountdown.value = RETRY_AFTER_SECONDS;
  checkBackendHealth();
}

function startCountdown() {
  if (countdownIntervalId != null) return;
  retryCountdown.value = RETRY_AFTER_SECONDS;
  countdownIntervalId = setInterval(() => {
    retryCountdown.value -= 1;
    if (retryCountdown.value <= 0) {
      retryCountdown.value = RETRY_AFTER_SECONDS;
      checkBackendHealth();
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownIntervalId != null) {
    clearInterval(countdownIntervalId);
    countdownIntervalId = null;
  }
  retryCountdown.value = RETRY_AFTER_SECONDS;
}

watch(
  () => networkStore.backendUnreachable,
  (unreachable) => {
    if (unreachable) {
      startCountdown();
    } else {
      stopCountdown();
    }
  }
);

function startPeriodicHealthCheck() {
  if (healthCheckIntervalId != null) return;
  healthCheckIntervalId = setInterval(checkBackendHealth, BACKEND_HEALTH_CHECK_INTERVAL_MS);
}

function stopPeriodicHealthCheck() {
  if (healthCheckIntervalId != null) {
    clearInterval(healthCheckIntervalId);
    healthCheckIntervalId = null;
  }
  stopCountdown();
}

onMounted(() => {
  checkBackendHealth();
  startPeriodicHealthCheck();
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
  stopPeriodicHealthCheck();
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
